import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export default function Nav() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const isCasa = useLocation().pathname.startsWith("/casa-de-leon");
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const brand = isCasa
    ? { name: "Casa de León", sub: "· Punta Arena" }
    : { name: "Punto Nima", sub: "· San José del Cabo" };

  const links = isCasa
    ? [
        ["The Villa", "residence"],
        ["Gallery", "gallery"],
        ["Rooms", "rooms"],
        ["Community", "resort"],
        ["Amenities", "amenities"],
        ["Location", "location"],
        ["Guide", "guide"],
        ["Reviews", "reviews"],
        ["Reserve", "booking"],
      ]
    : [
        ["The Residence", "residence"],
        ["Gallery", "gallery"],
        ["Rooms", "rooms"],
        ["Amenities", "amenities"],
        ["Resort", "resort"],
        ["Location", "location"],
        ["Guide", "guide"],
        ["Reserve", "booking"],
      ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-white/40 py-4" : "bg-transparent border-b border-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between">
        <div className="flex flex-col">
          <Link to="/" data-testid="nav-derron-collection"
            className={`hidden sm:flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase mb-0.5 transition-colors hover:text-[#B86B52] ${scrolled ? "text-[#5C5A56]" : "text-white/70"}`}>
            <ArrowUpLeft className="w-3 h-3" strokeWidth={1.5} /> {t("Derron Luxury Collection")}
          </Link>
          <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={`font-display text-2xl tracking-tight text-left ${scrolled ? "text-[#1A1918]" : "text-white"}`}>
            {brand.name} <span className="italic font-light">{t(brand.sub)}</span>
          </button>
        </div>
        <nav className="hidden md:flex items-center gap-9">
          {links.map(([label, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)}
              className={`text-sm tracking-wide transition-colors hover:text-[#B86B52] ${scrolled ? "text-[#1A1918]" : "text-white/90"}`}>
              {t(label)}
            </button>
          ))}
          <button data-testid="nav-lang-toggle" onClick={toggle} aria-label="Switch language"
            className={`text-xs tracking-[0.15em] font-medium transition-colors hover:text-[#B86B52] ${scrolled ? "text-[#1A1918]" : "text-white/90"}`}>
            <span className={lang === "en" ? "text-[#B86B52]" : ""}>EN</span>
            <span className="mx-1 opacity-40">/</span>
            <span className={lang === "es" ? "text-[#B86B52]" : ""}>ES</span>
          </button>
          <button data-testid="nav-book-cta" onClick={() => go("booking")}
            className="px-6 py-2.5 bg-[#B86B52] text-white text-sm tracking-wide rounded-sm hover:bg-[#a35c45] transition-colors">
            {t("Book Now")}
          </button>
        </nav>
        <div className="md:hidden flex items-center gap-4">
          <button data-testid="nav-lang-toggle-mobile" onClick={toggle} aria-label="Switch language"
            className={`text-xs tracking-[0.12em] font-medium ${scrolled ? "text-[#1A1918]" : "text-white/90"}`}>
            <span className={lang === "en" ? "text-[#B86B52]" : ""}>EN</span>
            <span className="mx-0.5 opacity-40">/</span>
            <span className={lang === "es" ? "text-[#B86B52]" : ""}>ES</span>
          </button>
          <button data-testid="nav-book-mobile" onClick={() => go("booking")}
            className="px-5 py-2 bg-[#B86B52] text-white text-sm rounded-sm">{t("Book")}</button>
        </div>
      </div>
    </motion.header>
  );
}
